// Personal (per-user) post-login gate data — profile completeness + pending
// KVKK/ETK consents. Mock-vs-real aware, mirroring useRequiredAgreements:
//
//   - real mode: read both off the ['me'] query (/auth/me carries `profile_complete`
//     + `pending_consents`); one cached fetch feeds both gates.
//   - mock mode: GET /profile/status + GET /consents/pending (MSW). Both default
//     to "no gate" so the mock role-switcher UX is never disrupted unless a test
//     forces a pending state.
//
// Fail-open on missing data: an absent flag never blocks — the gate engages only
// on an explicit `profile_complete === false` / a non-empty pending list.

import { useQuery, useQueryClient } from '@tanstack/react-query'
import { USE_REAL_API, apiUrl } from '../../lib/api'
import { fetchMe, type PendingConsent } from '../../lib/auth'
import { fetchPendingConsents } from '../../api/consents'

type ProfileStatus = {
  profile_complete: boolean
  display_name: string
  phone: string | null
}

export type PersonalGatesResult = {
  loading: boolean
  profileComplete: boolean
  displayName: string
  phone: string | null
  pendingConsents: PendingConsent[]
  refetch: () => void
}

async function fetchProfileStatus(): Promise<ProfileStatus> {
  const res = await fetch(apiUrl('/profile/status'))
  if (!res.ok) throw new Error(`profile status ${res.status}`)
  return (await res.json()) as ProfileStatus
}

export function usePersonalGates(enabled: boolean): PersonalGatesResult {
  const isMock = !USE_REAL_API
  const queryClient = useQueryClient()

  const meQ = useQuery({
    queryKey: ['me'],
    queryFn: fetchMe,
    enabled: enabled && !isMock,
    staleTime: 5 * 60 * 1000,
  })

  const profileQ = useQuery({
    queryKey: ['profile-status'],
    queryFn: fetchProfileStatus,
    enabled: enabled && isMock,
    retry: false,
    staleTime: 30 * 1000,
  })

  const consentsQ = useQuery({
    queryKey: ['consents', 'pending'],
    queryFn: fetchPendingConsents,
    enabled: enabled && isMock,
    retry: false,
    staleTime: 30 * 1000,
  })

  const profileComplete = isMock
    ? (profileQ.data?.profile_complete ?? true)
    : (meQ.data?.user?.profile_complete ?? true)
  const displayName = isMock
    ? (profileQ.data?.display_name ?? '')
    : (meQ.data?.user?.display_name ?? '')
  const phone = isMock
    ? (profileQ.data?.phone ?? null)
    : (meQ.data?.user?.phone ?? null)
  const pendingConsents = isMock
    ? (consentsQ.data ?? [])
    : (meQ.data?.pending_consents ?? [])

  const loading = enabled
    ? isMock
      ? profileQ.isLoading || consentsQ.isLoading
      : meQ.isLoading
    : false

  return {
    loading,
    profileComplete,
    displayName,
    phone,
    pendingConsents,
    refetch: () => {
      if (isMock) {
        void queryClient.invalidateQueries({ queryKey: ['profile-status'] })
        void queryClient.invalidateQueries({ queryKey: ['consents', 'pending'] })
      } else {
        void meQ.refetch()
      }
    },
  }
}
